import type { ApiProblem, SessionTokens } from "../domain/models";
import { secureStore } from "../platform/secureStorage";
import { tokensSchema } from "./schemas";

const API_URL = (
  import.meta.env.VITE_API_URL ?? "http://localhost:8080"
).replace(/\/$/, "");
const TOKEN_KEY = "spotit.session.v1";
let refreshPromise: Promise<SessionTokens | null> | null = null;
export class ApiError extends Error {
  constructor(readonly problem: ApiProblem) {
    super(problem.detail ?? problem.title);
  }
}
export async function getSession() {
  const raw = await secureStore.get(TOKEN_KEY);
  if (!raw) return null;
  const parsed = tokensSchema.safeParse(JSON.parse(raw));
  return parsed.success ? parsed.data : null;
}
export async function setSession(tokens: SessionTokens | null) {
  if (tokens) await secureStore.set(TOKEN_KEY, JSON.stringify(tokens));
  else await secureStore.remove(TOKEN_KEY);
}
async function refresh() {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    const current = await getSession();
    if (!current) return null;
    const response = await fetch(`${API_URL}/api/v1/auth/refresh`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ refreshToken: current.refreshToken }),
    });
    if (!response.ok) {
      await setSession(null);
      window.dispatchEvent(new Event("spotit:session-revoked"));
      return null;
    }
    const next = tokensSchema.parse(await response.json());
    await setSession(next);
    return next;
  })().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}
async function send(
  path: string,
  init: RequestInit = {},
  retry = true,
): Promise<Response> {
  const session = await getSession();
  const headers = new Headers(init.headers);
  headers.set("accept", "application/json");
  headers.set("x-correlation-id", crypto.randomUUID());
  if (init.body) headers.set("content-type", "application/json");
  if (session) headers.set("authorization", `Bearer ${session.accessToken}`);
  const response = await fetch(`${API_URL}${path}`, { ...init, headers });
  if (response.status === 401 && retry && session) {
    const next = await refresh();
    if (next) return send(path, init, false);
  }
  if (!response.ok) {
    let body: Partial<ApiProblem> = {};
    try {
      body = (await response.json()) as Partial<ApiProblem>;
    } catch {
      body = {};
    }
    throw new ApiError({
      status: response.status,
      title: body.title ?? "Request failed",
      ...(body.detail ? { detail: body.detail } : {}),
      ...(body.errors ? { errors: body.errors } : {}),
    });
  }
  return response;
}
export async function request<T>(
  path: string,
  init: RequestInit = {},
  retry = true,
): Promise<T> {
  const response = await send(path, init, retry);
  if (
    response.status === 204 ||
    response.headers.get("content-length") === "0" ||
    !response.headers.get("content-type")?.includes("json")
  )
    return undefined as T;
  return response.json() as Promise<T>;
}
export async function requestAcceptedId(
  path: string,
  init: RequestInit = {},
): Promise<string> {
  const response = await send(path, init);
  const location = response.headers.get("location");
  const id = location?.split("/").filter(Boolean).at(-1);
  if (!id)
    throw new ApiError({
      status: 502,
      title: "Invalid reservation response",
      detail:
        "The server accepted the request without returning its reservation location.",
    });
  return id;
}
